import { Fragment, useCallback, useEffect, useRef, useState } from 'react';
import { addressLine, businessRules } from '../lib/taxes.ts';
import { createPortal } from 'react-dom';
import { t, tn } from '../i18n/index.ts';
import { action } from '../lib/api.ts';
import { useSession } from '../lib/session.tsx';
import { dateTime, money, qty } from '../lib/format.ts';
import { checkSoon, useReceiptPrinter } from '../lib/printing.ts';
import type { FiscalStamp, Receipt, ReceiptLoyalty } from '../lib/types.ts';
import { linePrice } from '../lib/taxes.ts';
import { useUi } from '../lib/ui-state.tsx';
import { Icon } from './Icon.tsx';
import { Button, Dialog, Skeleton } from './ui.tsx';

const METHOD = {
  cash: 'pay.method_cash',
  card: 'pay.method_card',
  other: 'pay.method_other',
  gift_card: 'pay.method_gift_card',
  points: 'pay.method_points',
} as const satisfies Record<Receipt['payments'][number]['method'], string>;

/** The receipt as it comes out of an 80 mm printer. Also used for the on-screen preview. */
export function ReceiptPaper({ r, sample, narrow }: { r: Receipt; /** A made-up receipt, marked so on the paper. */ sample?: boolean; /** 58 mm paper. */ narrow?: boolean }) {
  const c = r.company;
  const name = c.trade_name || c.legal_name;
  const at = r.location;
  // A business with several locations prints the one that sold, with its own address and phone.
  const place = addressLine(at ?? c);
  const phone = at?.phone || c.phone;
  return (
    <article className={`paper${narrow ? ' paper-58' : ''}`} aria-label={r.kind === 'bill' ? t('receipt.bill') : t('receipt.receipt')}>
      <header className="paper-head">
        <strong className="paper-name">{name}</strong>
        {c.trade_name && c.trade_name !== c.legal_name ? <span>{c.legal_name}</span> : null}
        {at ? <strong className="paper-place">{at.name}</strong> : null}
        {place ? <span>{place}</span> : null}
        {c.tax_id ? <span>{t('receipt.tax_id', { label: r.tax_id_label ?? t('country.tax_no'), id: c.tax_id })}</span> : null}
        {c.vat_id ? <span>{t('receipt.tax_id', { label: businessRules()?.vatId?.label ?? t('business.vat_id'), id: c.vat_id })}</span> : null}
        {phone ? <span>{phone}</span> : null}
      </header>

      <div className="paper-title">
        <strong>
          {r.kind === 'bill'
            ? t('receipt.bill')
            : r.offline
              ? t('receipt.offline_title')
              : r.refund
              ? t('receipt.refund_no', { n: r.order.receipt_no ?? '' })
              : t('receipt.receipt_no', { n: r.order.receipt_no ?? '' })}
        </strong>
        {r.kind === 'bill' ? <span>{t('receipt.not_a_receipt')}</span> : null}
        {r.refund ? <span>{t('receipt.refund_of', { n: r.refund.receipt_no ?? '' })}</span> : null}
        {r.offline ? <strong className="paper-test">{t('receipt.offline')}</strong> : null}
        {sample ? <strong className="paper-test">{t('receipt.sample')}</strong> : null}
        {r.copy ? <span className="paper-copy">{t('receipt.copy')}</span> : null}
      </div>

      <dl className="paper-meta">
        <dt>{t('receipt.order')}</dt>
        <dd>{r.offline ? r.order.name : `#${r.order.number} · ${r.order.name}`}</dd>
        <dt>{t('receipt.date')}</dt>
        <dd>{dateTime(r.kind === 'receipt' && r.order.closed_at ? r.order.closed_at : r.printed_at)}</dd>
        {r.order.guests ? (
          <>
            <dt>{t('receipt.guests')}</dt>
            <dd>{r.order.guests}</dd>
          </>
        ) : null}
        {r.served_by ? (
          <>
            <dt>{t('receipt.served_by')}</dt>
            <dd>{r.served_by}</dd>
          </>
        ) : null}
        {r.kind === 'receipt' && r.cashier ? (
          <>
            <dt>{t('receipt.cashier')}</dt>
            <dd>{r.cashier}</dd>
          </>
        ) : null}
        {r.refund?.reason ? (
          <>
            <dt>{t('receipt.refund_reason')}</dt>
            <dd>{r.refund.reason}</dd>
          </>
        ) : null}
      </dl>

      <table className="paper-lines">
        <thead>
          <tr>
            <th scope="col">{t('receipt.item')}</th>
            <th scope="col" className="num">
              {t('receipt.amount')}
            </th>
          </tr>
        </thead>
        <tbody>
          {r.lines.map((l, i) => {
            if (l.combo) {
              // A combo menu: one price under its name, the items it held below.
              if (r.lines.findIndex((x) => x.combo === l.combo) !== i) return null;
              const parts = r.lines.filter((x) => x.combo === l.combo);
              const discount = parts.reduce((n, x) => n + x.discount, 0);
              return (
                <tr key={i}>
                  <td>
                    <span className="paper-item">
                      {qty(l.qty)} × {l.combo_name}
                    </span>
                    {l.price_rule ? <span className="paper-sub">{l.price_rule}</span> : null}
                    {parts.map((x, j) => (
                      <span key={j} className="paper-sub">
                        – {x.name}
                        {x.modifiers.length ? ` (${x.modifiers.join(', ')})` : ''}
                        {x.note ? ` “${x.note}”` : ''}
                      </span>
                    ))}
                    {discount ? <span className="paper-sub">{t('receipt.line_discount', { amount: money(discount) })}</span> : null}
                  </td>
                  <td className="num">{money(parts.reduce((n, x) => n + linePrice({ ...x, tax_added: r.tax_added }), 0))}</td>
                </tr>
              );
            }
            return (
              <tr key={i}>
                <td>
                  <span className="paper-item">
                    {qty(l.qty)} × {l.name}
                  </span>
                  {l.qty !== 1 ? <span className="paper-sub">{t('receipt.each', { price: money(l.unit_price) })}</span> : null}
                  {l.price_rule ? <span className="paper-sub">{l.price_rule}</span> : null}
                  {l.modifiers.length ? <span className="paper-sub">{l.modifiers.join(', ')}</span> : null}
                  {l.note ? <span className="paper-sub">“{l.note}”</span> : null}
                  {l.discount ? <span className="paper-sub">{t('receipt.line_discount', { amount: money(l.discount) })}</span> : null}
                </td>
                <td className="num">{money(linePrice({ ...l, tax_added: r.tax_added }))}</td>
              </tr>
            );
          })}
        </tbody>
      </table>

      <dl className="paper-totals">
        {r.discount || r.tax_added ? (
          <>
            <dt>{t('totals.subtotal')}</dt>
            <dd>{money(r.subtotal)}</dd>
          </>
        ) : null}
        {r.discount ? (
          <>
            <dt>{t('totals.discount')}</dt>
            <dd>−{money(r.discount)}</dd>
          </>
        ) : null}
        {r.tax_added ? (
          <>
            <dt>{t('totals.sales_tax')}</dt>
            <dd>{money(r.tax)}</dd>
          </>
        ) : null}
        <dt className="paper-total">{t('totals.total')}</dt>
        <dd className="paper-total">{money(r.total)}</dd>
        {r.tip ? (
          <>
            <dt>{t('totals.tip')}</dt>
            <dd>{money(r.tip)}</dd>
          </>
        ) : null}
      </dl>

      {/* With sales tax only the taxed amounts are listed; with VAT every rate, 0 % too. */}
      {r.tax_added && !r.vat.some((v) => v.rate > 0) ? null : (
      <table className="paper-vat">
        <caption>{t(r.tax_added ? 'totals.sales_tax' : 'receipt.vat_title')}</caption>
        <thead>
          <tr>
            <th scope="col">{t('receipt.vat_rate')}</th>
            <th scope="col" className="num">
              {t(r.tax_added ? 'receipt.taxable' : 'receipt.vat_net')}
            </th>
            <th scope="col" className="num">
              {t(r.tax_added ? 'receipt.tax' : 'receipt.vat_tax')}
            </th>
          </tr>
        </thead>
        <tbody>
          {r.vat.filter((v) => !r.tax_added || v.rate > 0).map((v) => (
            <tr key={v.rate}>
              <td>{v.rate}%</td>
              <td className="num">{money(v.net)}</td>
              <td className="num">{money(v.tax)}</td>
            </tr>
          ))}
        </tbody>
      </table>
      )}

      {r.payments.length ? (
        <dl className="paper-totals">
          {r.payments.map((p, i) => (
            <PaymentRows key={i} p={p} />
          ))}
          {r.change ? (
            <>
              <dt>{t('receipt.change')}</dt>
              <dd>{money(r.change)}</dd>
            </>
          ) : null}
        </dl>
      ) : null}

      {r.loyalty ? <LoyaltyBlock l={r.loyalty} /> : null}

      {r.fiscal ? <FiscalBlock f={r.fiscal} /> : null}

      <footer className="paper-foot">
        {c.receipt_footer ? <p>{c.receipt_footer}</p> : null}
        <p>{t('receipt.printed', { at: dateTime(r.printed_at) })}</p>
      </footer>
    </article>
  );
}

/** A QR code drawn as one SVG path, sharp on screen and on a thermal printer. */
export function QrCode({ rows, label }: { rows: string[]; label: string }) {
  const n = rows.length;
  let d = '';
  rows.forEach((row, y) => {
    for (let x = 0; x < n; x++) if (row[x] === '1') d += `M${x + 4} ${y + 4}h1v1h-1z`;
  });
  return (
    <svg className="qr" viewBox={`0 0 ${n + 8} ${n + 8}`} role="img" aria-label={label} shapeRendering="crispEdges">
      <rect width={n + 8} height={n + 8} className="qr-light" />
      <path d={d} className="qr-dark" />
    </svg>
  );
}

/** Albania: invoice number, NSLF, NIVF and the QR code to check the receipt with the tax office. */
function FiscalBlock({ f }: { f: FiscalStamp }) {
  return (
    <section className="paper-fiscal" aria-label={t('receipt.fiscal')}>
      {f.test ? <strong className="paper-test">{t('receipt.fiscal_test')}</strong> : null}
      <dl className="paper-meta">
        <dt>{t('receipt.fiscal_number')}</dt>
        <dd>{f.inv_num}</dd>
        <dt>{t('receipt.fiscal_operator')}</dt>
        <dd>{f.operator}</dd>
        <dt>{t('receipt.fiscal_unit')}</dt>
        <dd>{f.business_unit}</dd>
        <dt>{t('receipt.fiscal_tcr')}</dt>
        <dd>{f.tcr}</dd>
        <dt>{t('receipt.fiscal_software')}</dt>
        <dd>{f.software}</dd>
      </dl>
      <dl className="paper-codes">
        <dt>{t('receipt.fiscal_iic')}</dt>
        <dd>{f.iic}</dd>
        <dt>{t('receipt.fiscal_fic')}</dt>
        <dd>{f.fic ?? t('receipt.fiscal_fic_waiting')}</dd>
        {f.corrects ? (
          <>
            <dt>{t('receipt.fiscal_corrects')}</dt>
            <dd>{f.corrects}</dd>
          </>
        ) : null}
      </dl>
      <QrCode rows={f.qr} label={t('receipt.fiscal_qr')} />
    </section>
  );
}

/** Gift card balances left and the customer's points. */
function LoyaltyBlock({ l }: { l: ReceiptLoyalty }) {
  const p = l.points;
  return (
    <dl className="paper-totals paper-loyalty">
      {(l.gift_cards ?? []).map((g, i) => (
        <Fragment key={i}>
          <dt>{g.wallet ? t('receipt.wallet_left') : t('receipt.gift_left', { tail: g.tail })}</dt>
          <dd>{money(g.left)}</dd>
        </Fragment>
      ))}
      {p?.will_earn ? (
        <>
          <dt>{t('receipt.points_will_earn')}</dt>
          <dd>{p.will_earn}</dd>
        </>
      ) : null}
      {p?.used ? (
        <>
          <dt>{t('receipt.points_used')}</dt>
          <dd>{p.used}</dd>
        </>
      ) : null}
      {p?.earned ? (
        <>
          <dt>{t('receipt.points_earned')}</dt>
          <dd>{p.earned}</dd>
        </>
      ) : null}
      {p ? (
        <>
          <dt>{t('receipt.points_balance')}</dt>
          <dd>{p.balance}</dd>
        </>
      ) : null}
    </dl>
  );
}

function PaymentRows({ p }: { p: Receipt['payments'][number] }) {
  return (
    <>
      <dt>{p.method === 'gift_card' && p.reference === 'WALLET' ? t('receipt.wallet_paid') : p.method === 'gift_card' && p.reference ? t('receipt.gift_paid', { tail: p.reference.slice(-4) }) : t(METHOD[p.method])}</dt>
      <dd>{money(p.amount)}</dd>
      {p.received !== null && p.received !== p.amount ? (
        <>
          <dt className="paper-indent">{t('receipt.received')}</dt>
          <dd>{money(p.received)}</dd>
        </>
      ) : null}
    </>
  );
}

/**
 * Shows the receipt (or the bill of an open order) and prints it. With `printNow` the
 * first print starts straight away, which is what a cashier wants after a payment.
 */
export function ReceiptDialog({
  order,
  printNow,
  onClose,
  onRefund,
}: {
  order: string;
  printNow?: boolean;
  onClose: () => void;
  /** Offered on a paid sale to people who may give money back. */
  onRefund?: () => void;
}) {
  const { fail, toast } = useUi();
  const { can } = useSession();
  const [r, setR] = useState<Receipt | null>(null);
  const [printing, setPrinting] = useState<Receipt | null>(null);
  const [busy, setBusy] = useState(false);

  const printer = useReceiptPrinter();
  const network = printer.register;

  /** On the register's network printer, or with `here` through this device's print window. */
  const print = useCallback(
    async (here = false) => {
      setBusy(true);
      try {
        if (network && !here) {
          const res = await action<{ printer: string; receipt: Receipt }>('printing.receipt', { order, register: network.id });
          setR(res.receipt);
          toast(res.receipt.copy ? t('printing.sent_copy', { printer: res.printer }) : t('printing.sent', { printer: res.printer }));
          checkSoon();
        } else {
          // The server records the print first, so a copy is always marked as one.
          const printed = await action<Receipt>('sales.receipt', { order, print: true });
          setR(printed);
          setPrinting(printed);
        }
      } catch (err) {
        fail(err);
      } finally {
        setBusy(false);
      }
    },
    [order, fail, toast, network],
  );

  // Load once per order; an automatic print waits until it is known where this register prints.
  const started = useRef(false);
  useEffect(() => {
    if (started.current) return;
    if (printNow) {
      if (!printer.ready) return;
      started.current = true;
      void print();
    } else {
      started.current = true;
      action<Receipt>('sales.receipt', { order }).then(setR, (err) => {
        fail(err);
        onClose();
      });
    }
  }, [order, printer.ready]);

  // Wait for the print sheet to be on the page, then open the browser's print window.
  useEffect(() => {
    if (!printing) return;
    const id = requestAnimationFrame(() => {
      window.print();
      setPrinting(null);
      toast(printing.copy ? t('receipt.printed_copy') : printing.kind === 'bill' ? t('receipt.printed_bill') : t('receipt.printed_toast'));
    });
    return () => cancelAnimationFrame(id);
  }, [printing, toast]);

  return (
    <Dialog
      title={
        r?.kind === 'bill'
          ? t('receipt.bill_for', { name: r.order.name })
          : r?.refund
            ? t('receipt.refund_no', { n: r.order.receipt_no ?? '' })
            : t('receipt.title', { n: r?.order.receipt_no ?? '' })
      }
      onClose={onClose}
      size="sm"
      footer={
        <>
          <span className="muted">{r ? (r.prints ? tn('receipt.prints', r.prints) : t('receipt.not_printed')) : ''}</span>
          {onRefund && r?.kind === 'receipt' && !r.refund && can('sales.refund') ? (
            <Button variant="secondary" icon="undo" onClick={onRefund}>
              {t('receipt.refund')}
            </Button>
          ) : null}
          <Button variant="primary" icon={network ? 'printer' : 'receipt'} onClick={() => void print()} busy={busy} disabled={!r && !printNow} data-autofocus>
            {r && r.prints > 0 ? t('receipt.print_copy') : t('receipt.print')}
          </Button>
        </>
      }
    >
      <div className="paper-preview">{r ? <ReceiptPaper r={r} /> : <Skeleton h={420} />}</div>
      {network && r ? (
        <p className="print-here">
          <Icon name="printer" size={16} /> {t('printing.prints_on', { printer: network.name })}
          <button type="button" className="link-button" onClick={() => void print(true)} disabled={busy}>
            {t('printing.print_here')}
          </button>
        </p>
      ) : null}
      {printing ? createPortal(<div className="print-sheet"><ReceiptPaper r={printing} /></div>, document.body) : null}
    </Dialog>
  );
}
